import * as Sentry from "@sentry/browser";
import Chart from "chart.js";
import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { FullLayout, MiddleLayout } from "~/components/Layout";
import "./styles/palette.css";
import "./styles/theme.css";
import "./styles/intents.css";
import "./index.scss";
import { ErrorBoundary } from "~/lib/errorBoundary";
import { SessionProvider, useSession } from "~/modules/Profile/session";
import { GuestRoutes, AppRoutes } from "~/routes";
import "./styles/vendors.scss";
import { InitializingView } from "~/views/InitializingView";
import { BreadcrumbContext } from "./components/Breadcrumps";
import { queryClient } from "~/lib/queryClient";
import { usePageTracking } from "~/lib/usePageTracking";

Sentry.init({
  ...CONFIG.sentry,
  release: "dashboard@" + VERSION,
  enabled: !CONFIG.development,
  debug: CONFIG.development,
});

// Set default font styles for Chart.JS
Chart.defaults.global.defaultFontFamily = "'Montserrat', sans-serif";
Chart.defaults.global.defaultFontSize = 13;
Chart.defaults.global.defaultFontStyle = "500";

(window as any)["Sentry"] = Sentry;

const Application = () => {
  usePageTracking();
  let { session } = useSession();
  if (session.isPending) {
    return (
      <MiddleLayout>
        <InitializingView />
      </MiddleLayout>
    );
  } else if (session.user) {
    return (
      <BreadcrumbContext.Provider
        value={{
          breadcrumbs: [
            {
              to: "/",
              title: "Home",
            },
          ],
        }}
      >
        <FullLayout>
          <AppRoutes />
        </FullLayout>
      </BreadcrumbContext.Provider>
    );
  } else {
    return (
      <MiddleLayout variant="form">
        <GuestRoutes />
      </MiddleLayout>
    );
  }
};

const container = document.getElementById("application");

if (container) {
  const root = createRoot(container);

  root.render(
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ErrorBoundary>
          <SessionProvider>
            <Application />
          </SessionProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
